import React, { useEffect, useState, useRef, lazy } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building, Users, Home, Briefcase, MapPin, ShieldCheck, Award, TrendingUp, Layout, Star, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { StatCard } from '../components/StatCard';
import { ProjectCard } from '../components/ProjectCard';
import { AdvantageCard } from '../components/AdvantageCard';
import { SEOSchema } from '../components/SEOSchema';
import { useLanguage } from '../contexts/LanguageContext';
export function HomePage() {
  const {
    t,
    language,
    getSlug
  } = useLanguage();
  const heroRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const getLanguageLink = (slug: string) => {
    const localizedSlug = getSlug(slug);
    return `/${language}/${localizedSlug}`;
  };
  const heroSlides = [{
    image: 'https://cdn.magicpatterns.com/uploads/o5Z9JxZzsjk9ruapYPMYQc/micheile-henderson-SoT4-mZhyhE-unsplash.jpg',
    title: t('Investasi Properti yang Menguntungkan', 'Profitable Property Investment'),
    subtitle: t('Nilai investasi yang terus berkembang dengan lokasi strategis', 'Growing investment value with strategic locations')
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/hrg6vm68b8K85GaZQSWH1M/r-architecture-pzMghCq2kS0-unsplash.jpg',
    title: t('Desain Modern & Fungsional', 'Modern & Functional Design'),
    subtitle: t('Hunian dengan arsitektur kontemporer dan ruang yang optimal', 'Contemporary architecture with optimized living spaces')
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/3jmbJWxUy2fUjjTqenstha/avi-werde-hHz4yrvxwlA-unsplash.jpg',
    title: t('Hunian Premium Berkelas', 'Premium Class Residences'),
    subtitle: t('Kualitas terbaik dengan material premium dan finishing sempurna', 'Best quality with premium materials and perfect finishing')
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/k6o1NoUeGqwVQiRXjpE1jZ/dominik-mCQ-ykj6tQk-unsplash.jpg',
    title: t('Lingkungan Nyaman & Asri', 'Comfortable & Green Environment'),
    subtitle: t('Kawasan hijau dengan fasilitas lengkap untuk keluarga', 'Green areas with complete facilities for families')
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/kfdqHdwPJJrePBWmh1qLa1/lisa-anna-sZrCKrETK4I-unsplash.jpg',
    title: t('Interior Elegan & Berkualitas', 'Elegant & Quality Interiors'),
    subtitle: t('Desain interior modern dengan perhatian pada detail', 'Modern interior design with attention to detail')
  }];
  const bankLogos = [{
    name: 'Bank BTN',
    logo: 'https://cdn.magicpatterns.com/uploads/7DBHhwHFs4rm7gfWup7pxc/Bank_BTN_logo.png'
  }, {
    name: 'Bank BNI',
    logo: 'https://cdn.magicpatterns.com/uploads/5fSfY7iBGRtgkRdfnsEVk4/Bank_Negara_Indonesia_logo_(2004).svg.png'
  }, {
    name: 'Bank BRI',
    logo: 'https://cdn.magicpatterns.com/uploads/rWgi7mFAt3DzvzyomFhHoF/BRI_2025.png'
  }, {
    name: 'CIMB Niaga',
    logo: 'https://cdn.magicpatterns.com/uploads/uwkTzrj7DJsGbw1fnZJkpY/CIMB_Niaga_logo.svg'
  }, {
    name: 'Bank Panin',
    logo: 'https://cdn.magicpatterns.com/uploads/32gPYqzWsjnSzmZQH1ZiFE/panin-default.svg'
  }, {
    name: 'Bank Muamalat',
    logo: 'https://cdn.magicpatterns.com/uploads/nM6XjrvzLkDhBL8aoX5hT8/Muamalat_Logo.png'
  }, {
    name: 'Bank Nagari',
    logo: 'https://cdn.magicpatterns.com/uploads/iGL7iZbkFENAnFFAQyya5n/Bank_Nagari.svg.png'
  }, {
    name: 'Bank Syariah Indonesia',
    logo: 'https://cdn.magicpatterns.com/uploads/dvmuRH24XqAh43MqytdsDQ/bsi-optimized.png'
  }, {
    name: 'Bank Harda',
    logo: 'https://cdn.magicpatterns.com/uploads/cFHFpY8TUaZJDAgwFL855K/Bank-harda-international.png'
  }, {
    name: 'Bank Bumiputera',
    logo: 'https://cdn.magicpatterns.com/uploads/2cAvnsbKTybYqEJuRn1G2Z/Bumiputera.svg.png'
  }, {
    name: 'Bank BTN Syariah',
    logo: 'https://cdn.magicpatterns.com/uploads/2XY7AJgE7t9Zn3sSCcdV29/bank-btn-syariah-logo-png_seeklogo-452849.png'
  }, {
    name: 'Bank Syariah Mandiri',
    logo: 'https://cdn.magicpatterns.com/uploads/fFgJBS7wmB8iBozHSb5kDU/Bank_Syariah_Mandiri_logo.svg'
  }];
  // Auto-advance slider every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroSlides.length]);
  // Scroll reveal effect
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
        }
      });
    }, observerOptions);
    document.querySelectorAll('.scroll-reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  // Auto-scroll carousel with faster speed and drag/touch support
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    let scrollInterval: NodeJS.Timeout;
    let isAutoScrolling = true;
    const startAutoScroll = () => {
      if (!isAutoScrolling) return;
      scrollInterval = setInterval(() => {
        if (isDragging) return; // Don't auto-scroll while dragging
        if (carousel.scrollLeft >= carousel.scrollWidth - carousel.clientWidth) {
          carousel.scrollLeft = 0;
        } else {
          carousel.scrollLeft += 2; // Increased from 1 to 2 for faster scrolling
        }
      }, 15); // Decreased from 20 to 15 for faster scrolling
    };
    const stopAutoScroll = () => {
      isAutoScrolling = false;
      clearInterval(scrollInterval);
    };
    const resumeAutoScroll = () => {
      isAutoScrolling = true;
      startAutoScroll();
    };
    // Mouse drag functionality
    const handleMouseDown = (e: MouseEvent) => {
      setIsDragging(true);
      setStartX(e.pageX - carousel.offsetLeft);
      setScrollLeft(carousel.scrollLeft);
      carousel.style.cursor = 'grabbing';
      stopAutoScroll();
    };
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX - carousel.offsetLeft;
      const walk = (x - startX) * 2; // Multiply by 2 for faster drag
      carousel.scrollLeft = scrollLeft - walk;
    };
    const handleMouseUp = () => {
      setIsDragging(false);
      carousel.style.cursor = 'grab';
      setTimeout(resumeAutoScroll, 1000); // Resume auto-scroll after 1 second
    };
    const handleMouseLeave = () => {
      if (isDragging) {
        setIsDragging(false);
        carousel.style.cursor = 'grab';
        setTimeout(resumeAutoScroll, 1000);
      }
    };
    // Touch drag functionality for mobile
    const handleTouchStart = (e: TouchEvent) => {
      setIsDragging(true);
      setStartX(e.touches[0].pageX - carousel.offsetLeft);
      setScrollLeft(carousel.scrollLeft);
      stopAutoScroll();
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;
      const x = e.touches[0].pageX - carousel.offsetLeft;
      const walk = (x - startX) * 2;
      carousel.scrollLeft = scrollLeft - walk;
    };
    const handleTouchEnd = () => {
      setIsDragging(false);
      setTimeout(resumeAutoScroll, 1000);
    };
    carousel.style.cursor = 'grab';
    startAutoScroll();
    // Add event listeners
    carousel.addEventListener('mousedown', handleMouseDown);
    carousel.addEventListener('mousemove', handleMouseMove);
    carousel.addEventListener('mouseup', handleMouseUp);
    carousel.addEventListener('mouseleave', handleMouseLeave);
    carousel.addEventListener('touchstart', handleTouchStart);
    carousel.addEventListener('touchmove', handleTouchMove);
    carousel.addEventListener('touchend', handleTouchEnd);
    return () => {
      stopAutoScroll();
      carousel.removeEventListener('mousedown', handleMouseDown);
      carousel.removeEventListener('mousemove', handleMouseMove);
      carousel.removeEventListener('mouseup', handleMouseUp);
      carousel.removeEventListener('mouseleave', handleMouseLeave);
      carousel.removeEventListener('touchstart', handleTouchStart);
      carousel.removeEventListener('touchmove', handleTouchMove);
      carousel.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, startX, scrollLeft]);
  return <div className="flex flex-col min-h-screen" data-id="element-716">
      <SEOSchema type="home" title={t('PT Graha Riau Gemilang | Developer Properti Terpercaya di Riau', 'PT Graha Riau Gemilang | Trusted Real Estate Developer in Riau')} description={t('Developer properti terpercaya sejak 2005 di Riau. Menyediakan rumah subsidi, hunian premium, dan properti komersial dengan legalitas terjamin.', 'Established in 2005, PT Graha Riau Gemilang is a trusted real estate developer in Riau delivering quality residential and commercial properties.')} data-id="element-717" />

      {/* Hero Slider Section - Auto-play without buttons */}
      <section ref={heroRef} className="relative min-h-[600px] sm:min-h-[700px] md:min-h-[800px] flex items-center justify-center overflow-hidden" data-id="element-718">
        {/* Slides */}
        {heroSlides.map((slide, index) => <div key={index} className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`} data-id="element-719">
            {/* Background Image with Lazy Loading */}
            <div className="absolute inset-0 bg-gray-900" data-id="element-720">
              <img src={slide.image} alt={slide.title} className="w-full h-full object-cover" loading={index === 0 ? 'eager' : 'lazy'} data-id="element-721" />
              <div className="absolute inset-0 bg-gradient-to-r from-gray-900/60 via-gray-900/40 to-gray-900/30" data-id="element-722"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#C4161C]/10 via-transparent to-transparent opacity-30" data-id="element-723"></div>
            </div>

            {/* Content */}
            <div className="relative z-20 container mx-auto px-4 sm:px-6 lg:px-8 text-center md:text-left w-full h-full flex items-center py-12 sm:py-16 md:py-20" data-id="element-724">
              <div className="max-w-4xl mx-auto md:mx-0" data-id="element-725">
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 md:mb-8 leading-tight text-white animate-fade-in-up" style={{
              animationDelay: '0.1s'
            }} data-id="element-726">
                  {slide.title}
                </h1>

                <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-200 mb-6 sm:mb-8 md:mb-10 leading-relaxed max-w-3xl animate-fade-in-up" style={{
              animationDelay: '0.2s'
            }} data-id="element-727">
                  {slide.subtitle}
                </p>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center md:justify-start animate-fade-in-up" style={{
              animationDelay: '0.3s'
            }} data-id="element-728">
                  <Link to={getLanguageLink('proyek')} data-id="element-729">
                    <Button size="lg" className="w-full sm:w-auto group text-sm sm:text-base" data-id="element-730">
                      <span data-id="element-731">
                        {t('Eksplorasi Proyek Kami', 'Explore Our Projects')}
                      </span>
                      <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" data-id="element-732" />
                    </Button>
                  </Link>
                  <Link to={getLanguageLink('kontak')} data-id="element-733">
                    <Button variant="outline" size="lg" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-[#C4161C] text-sm sm:text-base" data-id="element-734">
                      {t('Konsultasi Strategis', 'Strategic Consultation')}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>)}

        {/* Slide Indicators */}
        <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-30 flex gap-2" data-id="element-735">
          {heroSlides.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`h-2 rounded-full transition-all ${index === currentSlide ? 'bg-white w-6 sm:w-8' : 'bg-white/50 hover:bg-white/75 w-2'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-736" />)}
        </div>

        {/* Decorative bottom wave */}
        <div className="absolute bottom-0 left-0 right-0 h-16 sm:h-24 bg-gradient-to-t from-white to-transparent z-20" data-id="element-737"></div>
      </section>

      {/* Stats Section - Enhanced & Responsive */}
      <section className="relative -mt-12 sm:-mt-16 md:-mt-20 z-30 scroll-reveal px-4 sm:px-6 lg:px-8" data-id="element-738">
        <div className="max-w-7xl mx-auto" data-id="element-739">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-id="element-740">
            <StatCard icon={Award} number="20+" label={t('Tahun Pengalaman', 'Years of Experience')} description={t('Sejak berdiri tahun 2005 hingga kini', 'Established in 2005 and growing consistently')} data-id="element-741" />
            <StatCard icon={Briefcase} number="26+" label={t('Proyek Selesai', 'Completed Projects')} description={t('Di berbagai lokasi strategis Riau', 'Across strategic locations in Riau')} data-id="element-742" />
            <StatCard icon={Home} number="1774+" label={t('Unit Properti', 'Property Units Delivered')} description={t('Hunian dan properti komersial', 'Residential and commercial developments')} data-id="element-743" />
            <StatCard icon={Building} number="12+" label={t('Mitra Perbankan', 'Banking Partners')} description={t('Bank nasional & syariah terpercaya', 'Leading national and sharia banks')} data-id="element-744" />
          </div>
        </div>
      </section>

      {/* About Snippet - Enhanced with Owner Photo & Responsive */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-white to-gray-50 scroll-reveal px-4 sm:px-6 lg:px-8" data-id="element-745">
        <div className="max-w-7xl mx-auto" data-id="element-746">
          <div className="flex flex-col lg:flex-row items-center gap-8 sm:gap-12 lg:gap-16" data-id="element-747">
            <div className="w-full lg:w-1/2" data-id="element-748">
              <div className="relative rounded-xl overflow-hidden shadow-xl" data-id="element-749">
                <img src="https://cdn.magicpatterns.com/uploads/4tqCTGodELbZvAQ8LSXC2n/image.png" alt="Musdalil Amri, S.Si., M.M. - Direktur & Pendiri PT Graha Riau Gemilang" className="w-full h-auto object-cover" loading="lazy" data-id="element-750" />
              </div>
            </div>

            <div className="w-full lg:w-1/2" data-id="element-751">
              <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-red-50 text-[#C4161C] text-xs sm:text-sm font-bold mb-4 sm:mb-6" data-id="element-752">
                {t('Tentang Kami', 'About Us')}
              </div>

              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight" data-id="element-753">
                {t('Developer Properti Terpercaya dengan Pengalaman 20+ Tahun', 'Trusted Real Estate Developer with 20+ Years of Experience')}
              </h2>

              <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6 leading-relaxed" data-id="element-754">
                {t('PT Graha Riau Gemilang adalah pengembang properti terpercaya yang berkomitmen menghadirkan hunian berkualitas tinggi, legalitas terjamin, dan layanan profesional. Kami tidak hanya membangun rumah, tetapi menciptakan lingkungan hidup yang nyaman, aman, dan berkelanjutan.', 'PT Graha Riau Gemilang is a trusted real estate developer committed to delivering high-quality properties with clear legal compliance and professional service. We do not merely build houses — we develop living environments that support comfort, security, and sustainable growth.')}
              </p>

              <div className="bg-gradient-to-r from-blue-50 to-orange-50 border-l-4 border-[#C4161C] rounded-lg p-4 sm:p-6 mb-6 sm:mb-8" data-id="element-755">
                <div className="flex items-start space-x-3" data-id="element-756">
                  <img src="https://cdn.magicpatterns.com/uploads/xuBA4FLWNXtKdE4fyjLLfC/logoREI.png" alt="REI" className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0 mt-1" loading="lazy" data-id="element-757" />
                  <div data-id="element-758">
                    <h3 className="font-bold text-gray-900 mb-2 text-sm sm:text-base" data-id="element-759">
                      {t('Anggota Resmi REI', 'Official REI Member')}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-2" data-id="element-760">
                      {t('Sebagai anggota Real Estat Indonesia (REI), asosiasi pengusaha real estat terbesar di Indonesia yang didirikan tahun 1972, kami berkomitmen menjalankan praktik bisnis properti yang profesional, etis, dan sesuai standar industri nasional.', 'As a member of Real Estat Indonesia (REI), the largest real estate association in Indonesia established in 1972, we are committed to professional, ethical, and industry-standard property business practices.')}
                    </p>
                    <p className="text-xs text-gray-600 font-semibold" data-id="element-761">
                      {t('No. Registrasi: 09.00466', 'Registration No: 09.00466')}
                    </p>
                  </div>
                </div>
              </div>

              <Link to={getLanguageLink('tentang-kami')} data-id="element-762">
                <Button variant="secondary" className="group w-full sm:w-auto" data-id="element-763">
                  <span data-id="element-764">
                    {t('Selengkapnya Tentang Kami', 'Learn More About Us')}
                  </span>
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-765" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects - Enhanced with Real Data & Responsive */}
      <section className="py-12 sm:py-16 md:py-20 bg-white scroll-reveal px-4 sm:px-6 lg:px-8" data-id="element-766">
        <div className="max-w-7xl mx-auto" data-id="element-767">
          <div className="text-center mb-12 sm:mb-16" data-id="element-768">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-red-50 text-[#C4161C] text-xs sm:text-sm font-bold mb-4 sm:mb-6" data-id="element-769">
              {t('Portofolio', 'Portfolio')}
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight" data-id="element-770">
              {t('Proyek Unggulan', 'Featured Projects')}
            </h2>

            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed" data-id="element-771">
              {t('Proyek-proyek terbaru dan unggulan kami yang menampilkan hunian premium dan properti komersial berkualitas tinggi di lokasi strategis.', 'Our latest and featured projects showcasing premium residences and high-quality commercial properties in strategic locations.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12" data-id="element-772">
            {/* Featured Project 1 - Ongoing Premium */}
            <ProjectCard title="The Royal Padma" location="Pekanbaru, Riau" status={t('Berjalan', 'Ongoing') as any} type={t('Rumah Premium', 'Premium Residence')} imageColor="bg-violet-100" units="12" data-id="element-773" />

            {/* Featured Project 2 - Largest Premium Cluster */}
            <ProjectCard title="Graha Padma Rimbo Panjang" location="Rimbo Panjang, Kampar" status={t('Selesai', 'Completed') as any} type={t('Rumah Premium', 'Premium Residence')} imageColor="bg-blue-100" units="217" data-id="element-774" />

            {/* Featured Project 3 - Commercial */}
            <ProjectCard title="Royal Pasir Putih (Ruko)" location="Kampar, Riau" status={t('Berjalan', 'Ongoing') as any} type={t('Komersial', 'Commercial')} imageColor="bg-amber-100" units="6" data-id="element-775" />
          </div>

          <div className="text-center" data-id="element-776">
            <Link to={getLanguageLink('proyek')} data-id="element-777">
              <Button variant="outline" size="lg" className="group w-full sm:w-auto" data-id="element-778">
                <span data-id="element-779">
                  {t('Lihat Semua Proyek (26+)', 'View All Projects (26+)')}
                </span>
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-780" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Advantages - Enhanced & Responsive */}
      <section className="py-12 sm:py-16 md:py-20 bg-gray-900 text-white scroll-reveal px-4 sm:px-6 lg:px-8" data-id="element-781">
        <div className="max-w-7xl mx-auto" data-id="element-782">
          <div className="text-center mb-12 sm:mb-16" data-id="element-783">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/10 border border-white/20 text-white text-xs sm:text-sm font-bold mb-4 sm:mb-6" data-id="element-784">
              {t('Keunggulan', 'Advantages')}
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 leading-tight" data-id="element-785">
              {t('Mengapa Memilih Kami', 'Why Choose Us')}
            </h2>

            <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed" data-id="element-786">
              {t('Komitmen kami pada kualitas, legalitas, dan kepuasan pelanggan menjadikan kami pilihan terpercaya untuk properti Anda.', 'Our commitment to quality, legal compliance, and customer satisfaction makes us the trusted choice for your property needs.')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-787">
            <AdvantageCard icon={MapPin} title={t('Lokasi strategis dan mudah diakses', 'Strategically located developments')} data-id="element-788" />
            <AdvantageCard icon={ShieldCheck} title={t('Kualitas bangunan dengan material terbaik', 'High-quality construction and materials')} data-id="element-789" />
            <AdvantageCard icon={Award} title={t('Legalitas dan perizinan lengkap', 'Complete and transparent legal documentation')} data-id="element-790" />
            <AdvantageCard icon={Layout} title={t('Desain modern dan fungsional', 'Modern and functional designs')} data-id="element-791" />
            <AdvantageCard icon={TrendingUp} title={t('Harga kompetitif & bernilai investasi', 'Competitive pricing with strong investment value')} data-id="element-792" />
            <AdvantageCard icon={Star} title={t('Reputasi terpercaya lebih dari 20 tahun', 'Proven reputation for over 20 years')} data-id="element-793" />
          </div>
        </div>
      </section>

      {/* Banking Partners - Auto Sliding Carousel & Responsive */}
      <section className="py-12 sm:py-16 md:py-20 bg-gray-100 scroll-reveal overflow-hidden" data-id="element-794">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-795">
          <div className="text-center mb-8 sm:mb-12" data-id="element-796">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-red-50 text-[#C4161C] text-xs sm:text-sm font-bold mb-4 sm:mb-6" data-id="element-797">
              {t('Mitra Kami', 'Our Partners')}
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4 leading-tight" data-id="element-798">
              {t('Mitra Perbankan', 'Banking Partners')}
            </h2>

            <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto" data-id="element-799">
              {t('Kami bekerja sama dengan institusi perbankan nasional dan syariah terkemuka untuk memudahkan proses KPR/KPA.', 'We collaborate with leading national and sharia banks to provide fast, secure, and transparent mortgage facilities.')}
            </p>
          </div>

          {/* Auto-scrolling carousel with drag support */}
          <div className="relative" data-id="element-800">
            <div ref={carouselRef} className="flex gap-4 sm:gap-6 md:gap-8 overflow-x-hidden select-none" style={{
            scrollBehavior: isDragging ? 'auto' : 'smooth'
          }} data-id="element-801">
              {/* Duplicate logos for seamless loop */}
              {[...bankLogos, ...bankLogos].map((bank, index) => <div key={index} className="flex-shrink-0 w-32 sm:w-36 md:w-40 h-20 sm:h-22 md:h-24 flex items-center justify-center p-3 sm:p-4 bg-white rounded-lg border border-gray-200 shadow-sm pointer-events-none" data-id="element-802">
                  <img src={bank.logo} alt={bank.name} className="max-h-10 sm:max-h-12 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-250" loading="lazy" draggable="false" data-id="element-803" />
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - Enhanced & Responsive */}
      <section className="relative py-12 sm:py-16 md:py-20 overflow-hidden" data-id="element-804">
        <div className="absolute inset-0" data-id="element-805">
          <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/t8M3WBqwjEiqZoAMfoD99F/jakub-zerdzicki-bqUZEAeWuok-unsplash.jpg')] bg-cover bg-center" data-id="element-806"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/70 via-[#C4161C]/50 to-gray-900/70" data-id="element-807"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white" data-id="element-808">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 leading-tight drop-shadow-lg" data-id="element-809">
            {t('Wujudkan Hunian dan Investasi Terbaik Anda', 'Start Your Property Journey')}
            <br data-id="element-810" />
            {t('Bersama Kami', 'with Confidence')}
          </h2>

          <p className="text-base sm:text-lg md:text-xl mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed drop-shadow-md" data-id="element-811">
            {t('Konsultasikan kebutuhan properti Anda sekarang dengan tim profesional kami.', 'Consult your housing or investment needs with our professional team today.')}
          </p>

          <div className="flex justify-center max-w-2xl mx-auto" data-id="element-812">
            <a href="https://wa.me/62817203456" target="_blank" rel="noopener noreferrer" className="inline-block w-full sm:w-auto" data-id="element-813">
              <button className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-[#25D366] text-white hover:bg-[#20BA5A] border-2 border-white rounded-lg shadow-xl hover:shadow-2xl transition-all duration-250 font-bold text-sm sm:text-base md:text-lg flex items-center justify-center gap-2" data-id="element-814">
                <span data-id="element-815">{t('WhatsApp Konsultasi', 'WhatsApp Consultation')}</span>
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-816" />
              </button>
            </a>
          </div>
        </div>
      </section>
    </div>;
}